import { useEffect, useState } from 'react'

import { arrayValues } from '@/utils/array-values'
import { formatBrDateToIso } from '@/utils/format-br-date-to-iso'
import { getLast31DaysPeriod } from '@/utils/get-last-31-days-period'

export type BaseFilters = {
  dataDoItem: any
  operadora: any
  status: any
  grupoEconomico: any
  unidade: any
  colaboradores: any
  agrupamento?: any
}

type FetchAllFn = (payload: any) => Promise<{ items: any[]; total: number }>

type Options = {
  afterFilter?: () => void
  adjustEndDate?: boolean
  grupoEconomicoEmpty?: null | ''
  hasAgrupamento?: boolean
}

export const useCommonContext = (fetchAllFn: FetchAllFn, options: Options = {}) => {
  const { afterFilter, adjustEndDate = false, grupoEconomicoEmpty = null, hasAgrupamento = false } = options

  const [isLoading, setIsLoading] = useState(false)
  const [tableData, setTableData] = useState<any[]>([])
  const [limit, setLimit] = useState(50)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [currentFilters, setCurrentFilters] = useState<BaseFilters | undefined>(undefined)
  const [results, setResults] = useState<number>(0)
  const [activeDrawer, setActiveDrawer] = useState(false)
  const [selectItems, setSelectItems] = useState<any[]>([])

  const dates = getLast31DaysPeriod().split(' - ')
  const defaultStartDate = formatBrDateToIso(dates[0])
  const defaultEndDate = formatBrDateToIso(dates[1])
  const period = `${defaultStartDate} - ${defaultEndDate}`

  useEffect(() => {
    setCurrentFilters({
      dataDoItem: period,
      operadora: null,
      status: null,
      grupoEconomico: null,
      unidade: null,
      colaboradores: null,
      ...(hasAgrupamento && { agrupamento: null }),
    })
  }, [])

  const fetchData = async (filtros: BaseFilters) => {
    setIsLoading(true)

    let startDate = ''
    let endDate = ''

    if (filtros.dataDoItem) {
      const dates = filtros.dataDoItem.split(' - ')
      startDate = new Date(dates[0]).toISOString().split('T')[0]
      const endDateObj = new Date(dates[1])
      if (adjustEndDate) endDateObj.setUTCDate(endDateObj.getUTCDate() + 1)
      endDate = endDateObj.toISOString().split('T')[0]
    }

    const operadoraValues = arrayValues(filtros.operadora ?? null)
    const statusValues = arrayValues(filtros.status ?? null)
    const unidadeValues = arrayValues(filtros.unidade ?? null)
    const grupoEconomicoValues = filtros.grupoEconomico?.value
      ? [filtros.grupoEconomico.value]
      : grupoEconomicoEmpty

    const payload = {
      ...(hasAgrupamento && { agrupamento: filtros.agrupamento ?? null }),
      dataLiberacaoFinal: endDate,
      dataLiberacaoInicial: startDate,
      idsEmpregadores: grupoEconomicoValues,
      idsOperadoras: operadoraValues.length > 0 ? operadoraValues : null,
      idsUnidades: unidadeValues.length > 0 ? unidadeValues : null,
      paginaAtual: page,
      situacoes: statusValues.length > 0 ? statusValues : null,
      tamanhoPagina: limit,
      termo: filtros.colaboradores ?? null,
    }

    const allData = await fetchAllFn(payload)
    setTableData(allData.items)
    setResults(allData.total)
    setIsLoading(false)
  }

  const handleFilter = async (filtros: BaseFilters) => {
    setCurrentFilters(filtros)
    await fetchData(filtros)
    afterFilter?.()
  }

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit)
    setPage(1)
  }

  useEffect(() => {
    if (currentFilters) {
      fetchData(currentFilters)
    }
  }, [page, limit, search])

  const handleSelectItem = (item: string[], action: boolean) => {
    setSelectItems(prevItems => {
      if (action === false) {
        return prevItems.filter(i => i !== item)
      }
      return action ? [...prevItems, item] : prevItems
    })
  }

  return {
    activeDrawer,
    currentFilters,
    handleFilter,
    handleLimitChange,
    handleSelectItem,
    isLoading,
    limit,
    page,
    results,
    search,
    selectItems,
    setActiveDrawer,
    setCurrentFilters,
    setLimit,
    setPage,
    setResults,
    setSearch,
    setSelectItems,
    setTableData,
    tableData,
  }
}
